/**
 * Черновик по фото — правка строк перед созданием в StoreHouse (D184, D204).
 *
 * Строки прочитала модель, а подтверждает человек: строка, где товар не
 * выбран или есть замечание, подсвечена, и «Создать черновик» недоступна,
 * пока такие есть. Готовность строки и всего черновика считает сервер
 * (`ready`) — теми же правилами, что в боте.
 *
 * Каждая правка уходит на сервер сразу и возвращает черновик целиком: так
 * правка из бота и правка отсюда не расходятся, а итог пересчитывает сервер.
 */

import { useEffect, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '@shell/api';
import { portalApi, qty, rub } from '../../api';
import type { Goods, LinePatch, Message, Proposal, ProposalLine, Recipient } from '../../api';
import { Icon } from '../../icons';
import { Notice } from '../../ui';

export function ProposalEditor({
  receiptId,
  proposal,
}: {
  receiptId: number;
  proposal: Proposal;
}) {
  const queryClient = useQueryClient();
  const [problem, setProblem] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  // Суммы — только с флагом денег: без него сервер отдаёт `null`.
  const money =
    proposal.lines.length > 0
      ? proposal.lines.some((line) => line.net !== null)
      : proposal.total !== null || proposal.header.paper_total !== null;

  const apply = (next: Proposal) => {
    setProblem(null);
    queryClient.setQueryData(['receipts', 'detail', receiptId], (old: unknown) =>
      old ? { ...(old as object), proposal: next } : old,
    );
  };
  const fail = (error: ApiError) =>
    setProblem(error.detail ?? 'Правка не сохранилась. Попробуйте ещё раз.');
  const refreshAll = () => {
    void queryClient.invalidateQueries({ queryKey: ['receipts'] });
    void queryClient.invalidateQueries({ queryKey: ['today'] });
  };

  const patchLine = useMutation<Proposal, ApiError, { n: number; patch: LinePatch }>({
    mutationFn: ({ n, patch }) => portalApi.patchLine(proposal.id, n, patch),
    onSuccess: apply,
    onError: fail,
  });
  const deleteLine = useMutation<Proposal, ApiError, number>({
    mutationFn: (n) => portalApi.deleteLine(proposal.id, n),
    onSuccess: apply,
    onError: fail,
  });
  const addLine = useMutation<Proposal, ApiError, number>({
    mutationFn: (goodsId) => portalApi.addLine(proposal.id, goodsId),
    onSuccess: (next) => {
      apply(next);
      setAdding(false);
    },
    onError: fail,
  });
  const patchHeader = useMutation<
    Proposal,
    ApiError,
    { recipient?: number; number?: string; date?: string }
  >({
    mutationFn: (patch) => portalApi.patchProposal(proposal.id, patch),
    onSuccess: apply,
    onError: fail,
  });
  const finish = useMutation<Message, ApiError, 'create' | 'cancel'>({
    mutationFn: (what) =>
      what === 'create'
        ? portalApi.createDraft(proposal.id)
        : portalApi.cancelProposal(proposal.id),
    onSuccess: (data) => {
      setProblem(null);
      setDone(data.message);
      refreshAll();
    },
    onError: fail,
  });

  const recipients = useQuery<Recipient[], ApiError>({
    queryKey: ['recipients'],
    queryFn: () => portalApi.recipients(),
    staleTime: 10 * 60_000,
  });

  const busy =
    patchLine.isPending ||
    deleteLine.isPending ||
    addLine.isPending ||
    patchHeader.isPending ||
    finish.isPending;
  const notReady = proposal.lines.filter((line) => !line.ready).length;
  const total = money ? rub(proposal.total) : null;
  const paperTotal = money ? rub(proposal.header.paper_total) : null;
  // Расхождение с бумагой — главное, что видно в подвале: сходится итог —
  // черновик можно создавать, не сходится — видно, на сколько.
  const gap =
    money && proposal.total !== null && proposal.header.paper_total !== null
      ? Number(proposal.total) - Number(proposal.header.paper_total)
      : null;
  const agrees = gap !== null && Math.abs(gap) <= 1;
  // Разница ровно в одну строку — почти всегда лишняя строка с другой бумаги
  // (06.10.2026: просекко 895 ₽ на фото рядом с ТОРГ-12). Назвать её прямо.
  const extraLine =
    gap !== null && gap > 1
      ? proposal.lines.find(
          (line) => Math.abs(Number(line.gross ?? line.net ?? 0) - gap) <= 0.01,
        )
      : undefined;
  const otherPapers = proposal.header.other_papers ?? [];
  const vatTotal =
    money && proposal.total !== null && proposal.net_total !== null
      ? rub((Number(proposal.total) - Number(proposal.net_total)).toFixed(2))
      : null;

  return (
    <div className="proposal" aria-busy={busy}>
      <div className="panel__block">
        <h3 className="block-title">Черновик по фото</h3>
        <div className="proposal__header">
          <label className="mini-field">
            <span>Склад</span>
            <select
              className="select"
              value={proposal.header.recipient ?? ''}
              disabled={busy || recipients.isPending}
              onChange={(event) =>
                event.target.value &&
                patchHeader.mutate({ recipient: Number(event.target.value) })
              }
            >
              {proposal.header.recipient === null && <option value="">Выберите склад</option>}
              {proposal.header.recipient !== null &&
                !recipients.data?.some((r) => r.id === proposal.header.recipient) && (
                  <option value={proposal.header.recipient}>
                    {proposal.header.recipient_name ?? 'Склад'}
                  </option>
                )}
              {(recipients.data ?? []).map((recipient) => (
                <option key={recipient.id} value={recipient.id}>
                  {recipient.name}
                </option>
              ))}
            </select>
          </label>
          <label className="mini-field">
            <span>Номер</span>
            <CommitInput
              value={proposal.header.number ?? ''}
              disabled={busy}
              onCommit={(value) => patchHeader.mutate({ number: value })}
              aria-label="Номер накладной"
            />
          </label>
          <label className="mini-field">
            <span>Дата</span>
            <CommitInput
              type="date"
              value={proposal.header.date?.slice(0, 10) ?? ''}
              disabled={busy}
              onCommit={(value) => value && patchHeader.mutate({ date: value })}
              aria-label="Дата накладной"
            />
          </label>
        </div>
      </div>

      {otherPapers.map((paper, index) => (
        <p key={index} className="proposal__note" data-testid="other-paper">
          На фото есть ещё одна бумага
          {paper.total ? ` на ${rub(paper.total)}` : ''}
          {paper.names.length ? ` («${paper.names.join('», «')}»)` : ''}. Её строки в этот черновик
          не вошли. Если это отдельная накладная — сдайте её отдельно.
        </p>
      ))}

      <div className={`plines${money ? '' : ' plines--no-money'}`} role="table" aria-label="Строки черновика">
        <div className="pline pline--head" role="row">
          <span role="columnheader" className="pline__n">
            #
          </span>
          <span role="columnheader" className="pline__name">
            Товар
          </span>
          <span role="columnheader" className="pline__qty num">
            Кол-во
          </span>
          <span role="columnheader" className="pline__unit">
            Ед.
          </span>
          {money && (
            <>
              <span role="columnheader" className="pline__amount num">
                Без НДС
              </span>
              <span role="columnheader" className="pline__vat">
                НДС
              </span>
              <span role="columnheader" className="pline__gross num">
                С НДС
              </span>
            </>
          )}
          <span className="pline__del" />
        </div>
        {proposal.lines.map((line) => (
          <LineRow
            key={line.number}
            line={line}
            proposalId={proposal.id}
            money={money}
            vatRates={proposal.vat_rates}
            busy={busy}
            onPatch={(patch) => patchLine.mutate({ n: line.number, patch })}
            onDelete={() => deleteLine.mutate(line.number)}
          />
        ))}
        {adding ? (
          <div className="pline-fix">
            <GoodsPicker
              proposalId={proposal.id}
              busy={busy}
              onPick={(goods) => addLine.mutate(goods.goods_id)}
              onClose={() => setAdding(false)}
            />
          </div>
        ) : (
          <button
            type="button"
            className="add-line"
            disabled={busy}
            onClick={() => setAdding(true)}
          >
            <Icon name="plus" size={16} /> Добавить строку
          </button>
        )}
      </div>

      {problem && <Notice kind="error">{problem}</Notice>}
      {done && <Notice>{done}</Notice>}

      <footer className="proposal__foot">
        <div className="proposal__total">
          {total !== null && (
            <>
              <span className="faint">
                Без НДС {rub(proposal.net_total)} · НДС {vatTotal}
              </span>
              <span className="faint">Итого с НДС</span>
              <strong>{total}</strong>
              {paperTotal && gap !== null && (
                <span
                  className={`proposal__paper ${agrees ? 'proposal__paper--ok' : 'proposal__paper--off'}`}
                  data-testid="paper-total"
                >
                  {agrees
                    ? `✓ сходится с бумагой (${paperTotal})`
                    : extraLine
                      ? `на бумаге ${paperTotal} — разница ${rub(gap.toFixed(2))}, ровно строка ` +
                        `${extraLine.number} «${extraLine.goods_name || extraLine.paper_name}». ` +
                        'Если её нет в этой накладной — удалите строку (корзина справа).'
                      : `на бумаге ${paperTotal} — разница ${rub(Math.abs(gap).toFixed(2))}. ` +
                        'Поправьте суммы или НДС в строках.'}
                </span>
              )}
            </>
          )}
        </div>
        <div className="proposal__buttons">
          <button
            type="button"
            className="button"
            disabled={busy}
            onClick={() => finish.mutate('cancel')}
          >
            Отменить
          </button>
          <button
            type="button"
            className="button"
            disabled={busy}
            onClick={() => {
              // Каждое поле сохраняется на сервере само, когда из него
              // уходят. Кнопка дописывает поле, в котором стоит курсор, и
              // говорит это вслух: к накладной можно вернуться позже.
              (document.activeElement as HTMLElement | null)?.blur();
              setProblem(null);
              setDone('Сохранено. Черновик ждёт в «Ждут черновика» — вернитесь к нему, когда удобно.');
            }}
          >
            Сохранить
          </button>
          <button
            type="button"
            className="button button--primary"
            disabled={busy || !proposal.ready}
            onClick={() => finish.mutate('create')}
          >
            {finish.isPending && finish.variables === 'create'
              ? 'Создаю…'
              : 'Создать черновик в StoreHouse'}
          </button>
        </div>
        {!proposal.ready && (
          <p className="proposal__hint">
            {notReady > 0
              ? `Сначала поправьте строки, отмеченные жёлтым: ${notReady}.`
              : 'Сначала выберите склад, номер и дату.'}
          </p>
        )}
      </footer>
    </div>
  );
}

/* ── Строка ──────────────────────────────────────────────────────────── */

function LineRow({
  line,
  proposalId,
  money,
  vatRates,
  busy,
  onPatch,
  onDelete,
}: {
  line: ProposalLine;
  proposalId: number;
  money: boolean;
  vatRates: number[];
  busy: boolean;
  onPatch: (patch: LinePatch) => void;
  onDelete: () => void;
}) {
  const [choosing, setChoosing] = useState(false);
  const picking = choosing || !line.goods;

  return (
    <div className={`pline-group${line.ready ? '' : ' pline-group--warn'}`} role="rowgroup">
      <div className="pline" role="row" data-testid={`line-${line.number}`}>
        <span className="pline__n">{line.number}</span>
        <span className="pline__name">
          {line.goods ? (
            <button
              type="button"
              className="link-button"
              onClick={() => setChoosing((v) => !v)}
              disabled={busy}
              title="Выбрать другой товар"
            >
              {line.goods_name}
            </button>
          ) : (
            <span className="pline__missing">товар не выбран</span>
          )}
          {line.paper_name && <span className="pline__paper">на бумаге: {line.paper_name}</span>}
          {line.notes.map((note) => (
            <span key={note} className="pline__note">
              {note}
              {note.startsWith('пересчёт единиц') && (
                <button
                  type="button"
                  className="button button--small pline__confirm"
                  disabled={busy}
                  onClick={() => onPatch({ confirm: true })}
                  aria-label={`Количество верно, строка ${line.number}`}
                >
                  <Icon name="check" size={14} /> Верно
                </button>
              )}
            </span>
          ))}
        </span>
        <span className="pline__qty">
          <CommitInput
            className="input input--num"
            inputMode="decimal"
            value={line.quantity === null ? '' : qty(line.quantity).replace(/\s/g, '')}
            disabled={busy}
            onCommit={(value) => onPatch({ quantity: value })}
            aria-label={`Количество, строка ${line.number}`}
          />
        </span>
        <span className="pline__unit">{line.unit_name || '—'}</span>
        {money && (
          <span className="pline__amount">
            <CommitInput
              className="input input--num"
              inputMode="decimal"
              value={line.net === null ? '' : line.net.replace('.', ',')}
              disabled={busy}
              onCommit={(value) => onPatch({ amount: value })}
              aria-label={`Сумма без НДС, строка ${line.number}`}
            />
          </span>
        )}
        {money && (
          <span className="pline__vat">
            <select
              className="select select--small"
              value={line.vat_rate ?? 0}
              disabled={busy}
              onChange={(event) => onPatch({ vat_rate: Number(event.target.value) })}
              aria-label={`Ставка НДС, строка ${line.number}`}
            >
              {(vatRates.includes(line.vat_rate ?? 0)
                ? vatRates
                : [...vatRates, line.vat_rate ?? 0]
              ).map((rate) => (
                <option key={rate} value={rate}>
                  {rate === 0 ? 'без' : `${rate / 100} %`}
                </option>
              ))}
            </select>
          </span>
        )}
        {money && (
          <span className="pline__gross">
            <CommitInput
              className="input input--num"
              inputMode="decimal"
              value={line.gross === null ? '' : line.gross.replace('.', ',')}
              disabled={busy}
              onCommit={(value) => onPatch({ gross: value })}
              aria-label={`Сумма с НДС, строка ${line.number}`}
            />
          </span>
        )}
        <span className="pline__del">
          <button
            type="button"
            className="icon-button"
            onClick={onDelete}
            disabled={busy}
            aria-label={`Удалить строку ${line.number}`}
            title="Удалить строку"
          >
            <Icon name="trash" size={16} />
          </button>
        </span>
      </div>
      {picking && (
        <div className="pline-fix">
          <GoodsPicker
            proposalId={proposalId}
            lineNumber={line.number}
            busy={busy}
            onPick={(goods) => {
              onPatch({ goods_id: goods.goods_id });
              setChoosing(false);
            }}
            onClose={line.goods ? () => setChoosing(false) : undefined}
          />
        </div>
      )}
    </div>
  );
}

/* ── Выбор товара: подсказки и поиск ─────────────────────────────────── */

export function useDebounced(value: string, ms = 250): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

function GoodsPicker({
  proposalId,
  lineNumber,
  busy,
  onPick,
  onClose,
}: {
  proposalId: number;
  lineNumber?: number;
  busy: boolean;
  onPick: (goods: Goods) => void;
  onClose?: () => void;
}) {
  const [text, setText] = useState('');
  const q = useDebounced(text.trim());

  const suggestions = useQuery<Goods[], ApiError>({
    queryKey: ['receipts', 'suggestions', proposalId, lineNumber],
    queryFn: () => portalApi.suggestions(proposalId, lineNumber as number),
    enabled: lineNumber !== undefined,
    staleTime: 60_000,
  });
  const found = useQuery<Goods[], ApiError>({
    queryKey: ['goods', q],
    queryFn: () => portalApi.goods(q),
    enabled: q.length >= 2,
    staleTime: 60_000,
  });

  return (
    <div className="picker">
      {lineNumber !== undefined && (suggestions.data?.length ?? 0) > 0 && (
        <div className="picker__chips">
          <span className="faint">Похоже на:</span>
          {suggestions.data!.map((goods) => (
            <button
              key={goods.goods_id}
              type="button"
              className="suggestion"
              disabled={busy}
              onClick={() => onPick(goods)}
            >
              {goods.name}
              {goods.unit_name && <span className="suggestion__unit">{goods.unit_name}</span>}
            </button>
          ))}
        </div>
      )}
      <div className="picker__search">
        <label className="search search--small">
          <Icon name="search" size={14} />
          <input
            className="search__input"
            type="search"
            placeholder="Найти товар склада"
            value={text}
            onChange={(event) => setText(event.target.value)}
            aria-label="Найти товар склада"
            autoFocus={lineNumber === undefined}
          />
        </label>
        {onClose && (
          <button type="button" className="button button--small button--quiet" onClick={onClose}>
            Закрыть
          </button>
        )}
      </div>
      {q.length >= 2 && (
        <ul className="picker__results">
          {found.isPending ? (
            <li className="faint">Ищу…</li>
          ) : (found.data ?? []).length === 0 ? (
            <li className="faint">Ничего не нашлось — попробуйте часть названия.</li>
          ) : (
            found.data!.map((goods) => (
              <li key={goods.goods_id}>
                <button
                  type="button"
                  className="picker__result"
                  disabled={busy}
                  onClick={() => onPick(goods)}
                >
                  <span>{goods.name}</span>
                  <span className="faint">{goods.unit_name}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

/* ── Поле, которое сохраняет по Enter или уходу с поля ──────────────── */

function CommitInput({
  value,
  onCommit,
  className = 'input',
  ...rest
}: {
  value: string;
  onCommit: (value: string) => void;
  className?: string;
  type?: string;
  inputMode?: 'decimal' | 'text';
  disabled?: boolean;
  'aria-label'?: string;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  const commit = () => {
    if (draft.trim() !== value.trim()) onCommit(draft.trim());
  };
  return (
    <input
      className={className}
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') (event.target as HTMLInputElement).blur();
        if (event.key === 'Escape') setDraft(value);
      }}
      {...rest}
    />
  );
}
